import 'client-only';

import { Box, FactSlogan, Flex, freddyTheme, Typography } from '@coolblue-development/freddy';
import hoverOverApplicationExplainerTrigger from '@coolblue-development/ga4-events/triggers/VacancyApplicationExplainer/hoverOverApplicationExplainerTrigger';
import selectApplicationExplainerTrigger from '@coolblue-development/ga4-events/triggers/VacancyApplicationExplainer/selectApplicationExplainerTrigger';
import { useAnalytics } from '@coolblue-development/next-analytics/client';
import { Image } from '@coolblue-development/next-image/client';
import type { FlippingCard as FlippingCardType, Image as ImageType } from '@sharedTypes';
import { motion } from 'framer-motion';
import { useState } from 'react';

type Props = {
  onClick: () => void;
  isFlipped: boolean;
  image: ImageType;
  data: FlippingCardType;
  variant: 'carousel' | 'fullWidth';
  index?: number;
};

export const FlippingCard = ({ onClick, isFlipped, image, data, variant, index }: Props) => {
  const { content } = data;
  const { trackEvent } = useAnalytics();

  const [isAnimating, setIsAnimating] = useState(false);

  const handleClick = () => {
    if (!isAnimating) {
      onClick();
      setIsAnimating(true);
      trackEvent(selectApplicationExplainerTrigger());
    }
  };

  return (
    <Box
      sx={{
        position: 'relative',
        width: variant === 'carousel' ? '30.1rem' : '100%',
        height: variant === 'carousel' ? '21rem' : '20rem',
        perspective: '1000px',
        '&:hover': {
          cursor: 'pointer'
        }
      }}
      onClick={handleClick}
      onMouseEnter={() => {
        trackEvent(hoverOverApplicationExplainerTrigger({ item_id: index || -1 }));
      }}
    >
      <motion.figure
        style={{
          transformStyle: 'preserve-3d',
          width: '100%',
          height: '100%',
          margin: 0,
          border: `1px solid ${freddyTheme.color.border.interactiveBase}`,
          borderRadius: freddyTheme.borderRadius[100]
        }}
        initial={false}
        animate={{ rotateY: isFlipped ? 180 : 360 }}
        transition={{ duration: 0.6 }}
        onAnimationComplete={() => setIsAnimating(false)}
      >
        <FlippingCardFront>
          <Image
            objectFit='cover'
            imageId={image.id}
            width={variant === 'carousel' ? 301 : 720}
            height={variant === 'carousel' ? 200 : 150}
            loading='lazy'
            alt={image.alt}
            fileCollection='content'
          />
          <Flex
            backgroundColor='white'
            padding={freddyTheme.spacing[200]}
            sx={{
              position: 'absolute',
              bottom: 0,
              width: '100%',
              height: '6rem',
              alignItems: 'center'
            }}
          >
            <FactSlogan
              fact={content.factSlogan.fact}
              slogan={content.factSlogan.slogan}
              size='medium'
            />
          </Flex>
        </FlippingCardFront>

        <FlippingCardBack>
          <Typography variant='h3'>{content.factSlogan.fact}</Typography>
          <Typography>{content.description}</Typography>
        </FlippingCardBack>
      </motion.figure>
    </Box>
  );
};

const FlippingCardFront = ({ children }: { children: React.ReactNode }) => (
  <Flex
    direction='column'
    borderRadius={freddyTheme.borderRadius[100]}
    sx={{
      position: 'absolute',
      width: '100%',
      height: '100%',
      boxShadow: freddyTheme.shadow.subtle,
      overflow: 'hidden',
      '&:hover': {
        boxShadow: '0 2px 8px 0 rgb(0 0 0 / 20%)'
      }
    }}
  >
    {children}
  </Flex>
);

const FlippingCardBack = ({ children }: { children: React.ReactNode }) => (
  <Box
    borderRadius={100}
    backgroundColor='secondary'
    padding={500}
    sx={{
      position: 'absolute',
      backfaceVisibility: 'hidden',
      transform: 'rotateY(180deg)',
      height: '100%',
      width: '100%',
      '&:hover': {
        boxShadow: '0 2px 8px 0 rgb(0 0 0 / 20%)'
      }
    }}
  >
    {children}
  </Box>
);
